import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Popup } from 'semantic-ui-react';

import { checkDate, format, getDateRegex, isSameOrBefore, parse } from '../../services/dateUtils';
import ClearableInput from '../Input/ClearableInput';
import DatePicker from './DatePicker';

import { StyledRangeDatePicker } from './DatePicker.styles';

const RangeDatePicker = ({
    dateFromName = 'from',
    dateFromValue,
    dateToName = 'to',
    dateToValue,
    pattern = 'dd.MM.yy',
    placeholder = 'Выберите период',
    label,
    onChange,
    withSort,
    ...props
}) => {
    const [open, setOpen] = useState(false);

    const {
        dateRegex,
        regex
    } = useMemo(
        () => {
            const dateRegex = getDateRegex(pattern);
            /* eslint-disable-next-line */
            const regex = new RegExp(`^${dateRegex.source}.*${dateRegex.source}\$`);

            return {
                dateRegex,
                regex
            };
        },
        [pattern]
    );

    const bothDatesAreFilled = !!(dateFromValue && dateToValue);

    const text = useMemo(
        () => {
            if (!bothDatesAreFilled) {
                return dateFromValue && checkDate(dateFromValue)
                    ? `${format(dateFromValue, pattern)} - `
                    : '';
            }

            return [dateFromValue, dateToValue]
                .map(date => checkDate(date)
                    ? format(date, pattern)
                    : '...')
                .join(' - ');
        },
        [
            bothDatesAreFilled,
            dateFromValue,
            dateToValue,
            pattern
        ]
    );

    const [inputValue, setInputValue] = useState(text);

    const handleTextInput = useCallback(
        (e, { value }) => {
            setInputValue(value);

            if (!value && e.type === 'click') {
                setOpen(false);

                onChange({
                    [dateFromName]: '',
                    [dateToName]: ''
                });

                return;
            }

            const str = (value || '').trim();

            if (!value || !regex.test(str)) return;

            const [from, to] = str.match(dateRegex);

            if (!(from && to)) return;

            const dateFrom = parse(from, pattern);
            const dateTo = parse(to, pattern);

            if (!(checkDate(dateFrom) && checkDate(dateTo) && isSameOrBefore(dateFrom, dateTo))) return;

            onChange({
                [dateFromName]: dateFrom,
                [dateToName]: dateTo
            });
        },
        [
            pattern,
            regex,
            dateRegex,
            dateFromName,
            dateToName,
            onChange
        ]
    );

    const handleChange = useCallback(
        (e, { value }) => {
            e.stopPropagation();

            const [dateFrom, dateTo] = value;

            onChange({
                [dateFromName]: dateFrom,
                [dateToName]: dateTo
            });

            if (dateFrom && dateTo) {
                setOpen(false);
            }
        },
        [
            dateFromName,
            dateToName,
            onChange
        ]
    );

    useEffect(
        () => {
            setInputValue(text);
        },
        [text]
    );

    return (
        <StyledRangeDatePicker>
            {!!label && <label>{label}</label>}
            <Popup
                open={open}
                basic
                position='bottom left'
                closeOnTriggerMouseLeave={false}
                closeOnPortalMouseLeave={false}
                onClose={() => setOpen(false)}
                style={{ padding: '0', border: '0', backgroundColor: 'rgba(0,0,0,0)', marginTop: '4px'}}
                trigger={(
                    <ClearableInput
                        readOnly
                        withSort={withSort}
                        sizer={withSort}
                        compact
                        placeholder={placeholder}
                        value={inputValue}
                        onChange={handleTextInput}
                        onFocus={() => setOpen(true)}
                        onClickCapture={e => e.stopPropagation()}
                    />
                )}
            >
                <DatePicker
                    {...props}
                    name="rangeDatePicker"
                    width="auto"
                    hideCalendarIcon
                    startDate={dateFromValue}
                    endDate={dateToValue}
                    selectsRange
                    shouldCloseOnSelect={false}
                    showPopperArrow={false}
                    inline
                    onChange={handleChange}
                />
            </Popup>
        </StyledRangeDatePicker>
    );
};

export default RangeDatePicker;



// WEBPACK FOOTER //
// ./App/components/DatePicker/RangeDatePicker.jsx